import { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { CalendarIcon, Link, Loader, Typography } from '@la/ds-ui-components';
import { Stepper } from '@la/shared-components';
import { RegistrationType } from '@la/types';
import {
  formatAsUSD,
  isPlayerRegistration,
  isStaffRegistration,
} from '@la/utilities';
import ErrorCard from 'components/ErrorCard/ErrorCard';
import { useRegistration } from 'lib/context/RegistrationContext/RegistrationContext';
import useMediaQuery from 'lib/hooks/useMediaQuery';
import { breakpointQueries } from 'lib/media-queries/breakpoints';
import { formatDateRange } from 'lib/utils/dateUtils';
import { getLAHostnameParts } from 'lib/utils/urlUtils';
import { useGetCountriesAndAdministrativeDivisionsQuery } from 'redux/services/countryApi';
import {
  useGetTournamentRegistrantQuery,
  useGetUserRegistrationsQuery,
} from 'redux/services/tournamentApi';
import { DiscardChangesModal } from 'domains/RosterManagement/RosterRollover/DiscardChangesModal/DiscardChangesModal';
import MainContent from 'domains/Shell/MainContent/MainContent';
import { API_ERROR_MESSAGE } from 'domains/Tournaments/Registration/Registration';
import { WizardHeader } from 'domains/Tournaments/Registration/Wizard/components/WizardHeader/WizardHeader';
import { TOURNAMENT_NOT_FOUND_ERROR_MESSAGE } from 'domains/Tournaments/utils/errorMessages';
import { RegistrationHeader } from '../Tournaments/Registration/RegistrationHeader/RegistrationHeader';
import styles from './MemberRegistration.module.scss';
import { getCapacityStatusAndType } from './utils/capacity';
import * as S from './MemberRegistration.styles';

export type PlayerAndStaffRegistrantTypes = Extract<
  RegistrationType,
  | RegistrationType.Player
  | RegistrationType.PlayerFreeAgent
  | RegistrationType.Staff
  | RegistrationType.StaffFreeAgent
>;

const MemberRegistration = () => {
  const {
    currentStep,
    ctx,
    dispatch,
    existingRegistrationError,
    fileUploadFormFields,
    formFields,
    formSteps,
    hasRequestLoading,
    loggedInUserId,
    masterProgramId,
    masterProgramName,
    masterProgramLocationName,
    nonFileUploadFormFields,
    numberOfTotalSteps,
    onBackClick,
    selectedPlayer,
    stepNumber,
    steps,
    teamName,
    tournamentId,
    waivers,
    setTouched,
    hasErrors,
    onWaiversSubmit,
    submitRegistration,
    roleName,
    registrationOptions,
    setRegistrationOptions,
    loading: isRegistrationLoading,
    feeValue,
    isCartLoading,
    addToCart,
  } = useRegistration();
  const { subdomain } = getLAHostnameParts();
  const { tabletLandscapeUp } = breakpointQueries;
  const isTabletLandscapeUp = useMediaQuery(tabletLandscapeUp);
  const { showUpdatedECommerceInRegFlowContent } = useFlags();

  /**
   * Perform the query here so we can handle the loading and error states, as
   * well as to cache the data for use in child components (PlayerDetailsModal).
   */
  const { isLoading: isCountriesLoading, error: countriesError } =
    useGetCountriesAndAdministrativeDivisionsQuery();

  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);
  const navigate = useNavigate();
  const {
    data: tournament,
    error: tournamentError,
    isLoading: isTournamentLoading,
  } = useGetTournamentRegistrantQuery({
    tournamentId: tournamentId?.toString() ?? '',
    siteDomain: subdomain,
  });

  const { data: userRegistrations } = useGetUserRegistrationsQuery(
    { programId: ctx?.programId.toString() ?? '', siteDomain: subdomain },
    { skip: !ctx?.programId }
  );

  const { atCapacity, atCapacityType } = useMemo(() => {
    return getCapacityStatusAndType(
      ctx?.type,
      parseInt(ctx?.programId.toString() ?? '0'),
      parseInt(ctx?.teamId?.toString() ?? '0'),
      loggedInUserId,
      userRegistrations?.userRegistrations,
      tournament?.registrationSummary
    );
  }, [ctx, loggedInUserId, tournament?.registrationSummary, userRegistrations]);

  // Populating fields state with form fields data
  useEffect(() => {
    if (
      formFields &&
      Object.values(nonFileUploadFormFields).length === 0 &&
      Object.values(fileUploadFormFields).length === 0
    ) {
      for (const field of formFields.nonFileUploadFormFields) {
        dispatch({
          type: 'SET_NON_FILE_UPLOAD_FORM_FIELD',
          payload: {
            id: field.propertyDefinitionId,
            field,
          },
        });
      }

      for (const field of formFields.fileUploadFormFields) {
        dispatch({
          type: 'SET_FILE_UPLOAD_FORM_FIELD',
          payload: {
            id: field.propertyDefinitionId,
            field,
          },
        });
      }
    }
  }, [formFields, fileUploadFormFields, nonFileUploadFormFields, dispatch]);

  useEffect(() => {
    if (tournament && !registrationOptions) {
      setRegistrationOptions(tournament.registrationOptions);
    }
  }, [tournament, registrationOptions, setRegistrationOptions]);

  if (
    !ctx ||
    isTournamentLoading ||
    isCountriesLoading ||
    isRegistrationLoading
  ) {
    return (
      <MainContent centerContent={true}>
        <Loader description="Loading registration..." loading />
      </MainContent>
    );
  }

  if (isCartLoading) {
    return (
      <MainContent centerContent={true}>
        <Loader description="Adding registrations to cart..." loading />
      </MainContent>
    );
  }

  if (tournamentError || countriesError) {
    return (
      <MainContent>
        <div className={styles.registrationContent}>
          <ErrorCard message={API_ERROR_MESSAGE} />
        </div>
      </MainContent>
    );
  }

  if (!tournament) {
    throw new Error(TOURNAMENT_NOT_FOUND_ERROR_MESSAGE);
  }

  if (!formFields || !waivers) {
    throw new Error('temporary error');
  }

  const { name, startDate, endDate } = tournament;

  const getTitle = () => {
    const value =
      !feeValue || isNaN(+feeValue) ? 'Free' : formatAsUSD(+feeValue);

    if (isPlayerRegistration(ctx.type)) {
      const isFreeAgent = ctx.type === RegistrationType.PlayerFreeAgent;
      return (
        <Typography variant="ui" size="xl" weight="bold">
          {isFreeAgent ? 'Free Agent' : 'Team'} Player{' '}
          {feeValue !== undefined ? (
            <>
              <S.PriceBullet>•</S.PriceBullet> {value}
            </>
          ) : null}
        </Typography>
      );
    }

    if (isStaffRegistration(ctx.type)) {
      const isFreeAgent = ctx.type === RegistrationType.StaffFreeAgent;
      return (
        <Typography variant="ui" size="xl">
          <strong>{isFreeAgent ? 'Free Agent' : 'Team'} staff member</strong> (
          {roleName})
          {feeValue !== undefined ? (
            <>
              <S.PriceBullet>•</S.PriceBullet> <strong>{value}</strong>
            </>
          ) : null}
        </Typography>
      );
    }
  };

  const title = (
    <S.TitleContainer $isTabletLandscapeUp={isTabletLandscapeUp}>
      <Typography variant="headline" size="xl" weight="bold">
        Registration
      </Typography>
      {getTitle()}
    </S.TitleContainer>
  );

  const formattedDate = formatDateRange(
    DateTime.fromISO(startDate),
    endDate ? DateTime.fromISO(endDate) : undefined
  );

  const composedHeader = (
    <div className={styles.composedHeaderContainer}>
      <Typography
        variant="ui"
        size={isTabletLandscapeUp ? 'xl' : 'large'}
        customTag="h2"
      >
        <strong>
          {teamName} {name}
        </strong>
        {' • '}
        <strong>{masterProgramName}</strong>
      </Typography>
      <S.ComposedHeaderDate $isTabletLandscapeUp={isTabletLandscapeUp}>
        <CalendarIcon
          fill="var(--blue-grey-400)"
          size="medium"
          variant="filled"
        />
        {formattedDate}
      </S.ComposedHeaderDate>
    </div>
  );

  const checkTouched = () => {
    if (isPlayerRegistration(ctx.type) && !selectedPlayer) {
      setTouched(true);
    }
  };

  const handleNextClick = () => {
    if (formSteps[currentStep]) {
      checkTouched();
    }

    if (currentStep === 'waivers') {
      onWaiversSubmit();
    }

    if (currentStep === 'review') {
      submitRegistration();
    }
  };

  let nextButtonFormProps = {};
  if (formSteps[currentStep]) {
    nextButtonFormProps = { form: formSteps[currentStep] };
  }

  if (atCapacity && atCapacityType) {
    window.location.href = `/app/tournaments/${masterProgramId}/select-registration-type?atCapacity=${atCapacityType}`;
  }

  const navigateToDashboard = () => {
    navigate(`/app/tournaments/${masterProgramId}`);
  };

  if (existingRegistrationError && isStaffRegistration(ctx.type)) {
    return (
      <MainContent>
        <WizardHeader
          name={name}
          title={title}
          startDate={DateTime.fromISO(startDate)}
          endDate={endDate ? DateTime.fromISO(endDate) : undefined}
          composedHeader={composedHeader}
          alignTitleLeft={true}
        />
        <div className={styles.registrationContent}>
          <ErrorCard
            message={
              <>
                {existingRegistrationError}{' '}
                <Link href="/dashboard" isBold size="large" variant="text">
                  View dashboard
                </Link>
              </>
            }
          />
        </div>
      </MainContent>
    );
  }

  return (
    <MainContent>
      {showUpdatedECommerceInRegFlowContent ? (
        <RegistrationHeader
          teamName={teamName}
          title={getPageTitle(ctx?.type as PlayerAndStaffRegistrantTypes)}
          tournamentEndDate={endDate ? DateTime.fromISO(endDate) : undefined}
          tournamentLocation={masterProgramLocationName}
          tournamentName={name}
          tournamentStartDate={DateTime.fromISO(startDate)}
        />
      ) : (
        <WizardHeader
          name={name}
          title={title}
          startDate={DateTime.fromISO(startDate)}
          endDate={endDate ? DateTime.fromISO(endDate) : undefined}
          composedHeader={composedHeader}
          alignTitleLeft={true}
        />
      )}
      <div className={styles.mainContentContainer}>
        <Stepper
          addToCart={addToCart}
          currentStep={steps[currentStep]}
          error={hasErrors}
          form={nextButtonFormProps}
          handleNextClick={handleNextClick}
          isMC={false}
          numberOfTotalSteps={numberOfTotalSteps}
          onBackClick={onBackClick}
          onCancelButtonClick={() => {
            setShowDiscardChangesModal(true);
          }}
          showBackButton={true}
          showDrawer={currentStep !== 'success'}
          showLoadingNextAction={hasRequestLoading}
          showSteps={currentStep !== 'success'}
          stepNumber={stepNumber}
          type={formSteps[currentStep] ? 'submit' : 'button'}
        >
          <div className={styles.outletContainer}>
            <Outlet />
          </div>
        </Stepper>
      </div>
      {showDiscardChangesModal ? (
        <DiscardChangesModal
          onDiscardChangesClick={navigateToDashboard}
          onOpenChange={setShowDiscardChangesModal}
          open={showDiscardChangesModal}
        />
      ) : null}
    </MainContent>
  );
};

export { MemberRegistration };

export function getPageTitle(
  registrationType?: PlayerAndStaffRegistrantTypes | null
) {
  const registrationTypeDictionary = {
    [RegistrationType.PlayerFreeAgent]: 'Free Agent Player',
    [RegistrationType.Player]: 'Player',
    [RegistrationType.StaffFreeAgent]: 'Free Agent Staff Member',
    [RegistrationType.Staff]: 'Staff Member',
  };

  return registrationType
    ? `Register a ${registrationTypeDictionary[registrationType]}`
    : 'Register';
}
